import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "../../../api-client";
import { queryKeys } from "../../query-keys";

/** The entity kinds the moderation endpoints accept. */
export type ModeratedType = "game" | "club";

export type ModerationTarget = { id: string; type: ModeratedType };

/**
 * List key of the entity itself, so approving/rejecting from a list page
 * refreshes that list and not just the moderation queue.
 */
function entityListKey(type: ModeratedType) {
	switch (type) {
		case "game":
			return queryKeys.games.all;
		case "club":
			return queryKeys.clubs.all;
	}
}

function useModerationMutation(mutationFn: (target: ModerationTarget) => Promise<void>) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn,
		onSuccess: (_data, target) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.moderation.all });
			queryClient.invalidateQueries({ queryKey: entityListKey(target.type) });
		},
	});
}

export function useModerationQueue() {
	return useQuery({
		queryFn: async () => {
			const [games, clubs] = await Promise.all([
				adminApi.moderation.listGames(),
				adminApi.moderation.listClubs(),
			]);
			return {
				items: [...games.items, ...clubs.items],
				limit: games.limit,
				offset: games.offset,
				total: games.total + clubs.total,
			};
		},
		queryKey: queryKeys.moderation.all,
	});
}

export function useApproveItem() {
	return useModerationMutation(({ id, type }) =>
		type === "game" ? adminApi.moderation.approveGame(id) : adminApi.moderation.approveClub(id),
	);
}

export function useRejectItem() {
	return useModerationMutation(({ id, type }) =>
		type === "game" ? adminApi.moderation.rejectGame(id) : adminApi.moderation.rejectClub(id),
	);
}
